import './scss/libs.scss'
import '@fontsource-variable/dm-sans'
import '@fontsource-variable/jetbrains-mono'
import '@fontsource/big-shoulders-display/500.css'
import '@fontsource/big-shoulders-display/700.css'
import '@fontsource/big-shoulders-display/900.css'
import './js/theme.js'
import './scss/theme.scss'
import { createApp, reactive } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'
import miniToastr from 'mini-toastr'
import routes from './routes'
import { services } from './services'
import { EventBus } from './bus'
import { identify } from './utils/analytics'
import filters from './filters'
import App from './App.vue'

// `env.X` literals are compile-time defines (vite.config APP_ENV_KEYS — every
// key is always defined, '' when unset). Do NOT read them through an alias
// object: define only rewrites literal `env.X` member expressions.
const APP_ENV = {
    PROTOCOL: env.PROTOCOL || (typeof window !== 'undefined' ? window.location.protocol.replace(':', '') : 'https'),
    DOMAIN: env.DOMAIN || (typeof window !== 'undefined' ? window.location.hostname : 'asoradar.com'),
    SITE_NAME: env.SITE_NAME || 'ASORadar',
    SITE_EMAIL: env.SITE_EMAIL || 'support@asoradar.com',
    TG_SUPPORT: env.TG_SUPPORT || 'asoradar',
    CF_SITE_KEY: env.CF_SITE_KEY || '',
    TG_BOT_NAME: env.TG_BOT_NAME || '',
}

const toastTypes = {
    success: 'success',
    error: 'error',
    info: 'info',
    warn: 'warn'
}
miniToastr.init({ types: toastTypes });

function toast({ title, message, type, timeout, cb }) {
    if (type == 'error')
        timeout = 100000000000;
    return miniToastr[type](message, title, timeout, cb)
}

const defaultMeta = {
    title: 'ASORadar - App Reviews API',
    description: 'Collect and analyze reviews from App Store and Google Play. Simple REST API with pay-per-request pricing. From $0.001 per request.',
    keywords: 'App Store API, Google Play API, app reviews API, app reviews scraper, ASO tools, app analytics API',
    image: ''
}

function updateMetaTag(name, content, isProperty = false) {
    const attr = isProperty ? 'property' : 'name'
    let element = document.querySelector(`meta[${attr}="${name}"]`)
    if (element) {
        element.setAttribute('content', content)
    } else {
        element = document.createElement('meta')
        element.setAttribute(attr, name)
        element.setAttribute('content', content)
        document.head.appendChild(element)
    }
}

function updateCanonicalUrl(path) {
    const baseUrl = `${APP_ENV.PROTOCOL}://${APP_ENV.DOMAIN}`
    const canonicalUrl = path === '/' ? baseUrl + '/' : baseUrl + path
    let link = document.querySelector('link[rel="canonical"]')
    if (link) {
        link.setAttribute('href', canonicalUrl)
    } else {
        link = document.createElement('link')
        link.setAttribute('rel', 'canonical')
        link.setAttribute('href', canonicalUrl)
        document.head.appendChild(link)
    }
    updateMetaTag('og:url', canonicalUrl, true)
    updateMetaTag('twitter:url', canonicalUrl)
}

function updateStructuredData(meta, path) {
    const baseUrl = `${APP_ENV.PROTOCOL}://${APP_ENV.DOMAIN}`
    const canonicalUrl = path === '/' ? `${baseUrl}/` : `${baseUrl}${path}`
    const title = meta.title ? `ASORadar | ${meta.title}` : defaultMeta.title
    const description = meta.description || defaultMeta.description
    const noindex = meta.noindex === true

    const graph = [{
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: APP_ENV.SITE_NAME || 'ASORadar',
        url: baseUrl,
    }]

    if (!noindex) {
        graph.push({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: title,
            description,
            url: canonicalUrl
        })
    }

    let script = document.querySelector('script[data-seo-ld="main"]')
    if (!script) {
        script = document.createElement('script')
        script.setAttribute('type', 'application/ld+json')
        script.setAttribute('data-seo-ld', 'main')
        document.head.appendChild(script)
    }
    script.textContent = JSON.stringify(graph)
}

function updateSEOMeta(meta, path) {
    const siteName = APP_ENV.SITE_NAME || 'ASORadar'
    const title = meta.title ? `${siteName} | ${meta.title}` : siteName
    const description = meta.description || defaultMeta.description
    const keywords = meta.keywords || defaultMeta.keywords

    document.title = title
    updateMetaTag('title', title)
    updateMetaTag('description', description)
    updateMetaTag('keywords', keywords)

    if (meta.noindex) {
        updateMetaTag('robots', 'noindex, nofollow')
    } else {
        updateMetaTag('robots', 'index, follow')
    }

    updateMetaTag('og:title', meta.title || defaultMeta.title, true)
    updateMetaTag('og:description', description, true)
    updateMetaTag('og:type', 'website', true)
    updateMetaTag('og:site_name', siteName, true)

    updateMetaTag('twitter:title', meta.title || defaultMeta.title)
    updateMetaTag('twitter:description', description)
    updateMetaTag('twitter:card', 'summary_large_image')

    // Social preview image (matches the SSR templates' og-card).
    const ogImage = `${APP_ENV.PROTOCOL}://${APP_ENV.DOMAIN}/assets/img/og-card.jpg`
    updateMetaTag('og:image', ogImage, true)
    updateMetaTag('twitter:image', ogImage)

    updateCanonicalUrl(path)
    updateStructuredData(meta, path)
}

function buildTitle(title) {
    document.title = `${document.title.split('|')[0]} | ${title}`
}

const unsecuredCopyToClipboard = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    try {
        document.execCommand('copy');
    } catch(err) {
        console.error('Unable to copy to clipboard', err);
    }
    document.body.removeChild(textArea);
};

window.getFilter = function(namespace, keyName, defaultValue) {
    let data = JSON.parse(localStorage.getItem(namespace + '_filter') || '{}');
    if (keyName) {
        return data[keyName] || defaultValue
    }
    return data
}
window.setFilter = function(namespace, data) {
    let current = window.getFilter(namespace);
    for (let key in data)
        current[key] = data[key];
    localStorage.setItem(namespace + '_filter', JSON.stringify(current));
    return true;
}

const router = createRouter({
    history: createWebHistory(),
    routes,
    linkActiveClass: 'active',
    scrollBehavior(to, from, savedPosition) {
        if (to.hash) return { el: to.hash, top: 90 } // clear StatusBar + navbar
        return savedPosition || { left: 0, top: 0 }
    }
})

router.beforeEach((to, from, next) => {
    updateSEOMeta(to.meta || {}, to.path)
    next()
})

const app = createApp(App)

const globalState = reactive({
    context: {
        loaded: null,
    },
    siteProto: APP_ENV.PROTOCOL,
    siteDomain: APP_ENV.DOMAIN,
    siteName: APP_ENV.SITE_NAME,
    siteEmail: APP_ENV.SITE_EMAIL,
    siteTgSupport: APP_ENV.TG_SUPPORT,
    cfSiteKey: APP_ENV.CF_SITE_KEY,
    capiUrl: "/capi/",
    version: typeof __VERSION__ !== 'undefined' ? __VERSION__ : 'dev',
    theme: (typeof document !== 'undefined' && document.documentElement.dataset.theme) || 'cyan',
})

const globalMethods = {
    setTheme(name) {
        const valid = ['cyan', 'amber', 'phosphor', 'crimson', 'sodium', 'ultraviolet'];
        if (valid.indexOf(name) < 0) return;
        document.documentElement.setAttribute('data-theme', name);
        globalState.theme = name;
        try { localStorage.setItem('asoradar.theme', name); } catch (e) { /* persistence best-effort */ }
    },
    copyToClipboard(content) {
        if (window.isSecureContext && navigator.clipboard) {
            navigator.clipboard.writeText(content);
        } else {
            unsecuredCopyToClipboard(content);
        }
        return true;
    },
    raise404() {
        router.push({ name: '404' });
    },
    getContext() {
        services.Context().get().then(
            resp => globalMethods.updateContext(resp.body),
            _ => globalMethods.updateContext({})
        );
    },
    informContext() {
        EventBus.emit('context:loaded', globalState.context);
    },
    updateContext(context) {
        const deflt = {
            loaded: true,
            promocode: localStorage.getItem("promocode"),
            referrer: localStorage.getItem("referrer")
        }
        Object.assign(globalState.context, deflt, context);
        // GA4 user_id — centralized here so components never call identify()
        // themselves (HK lesson: duplicated identify calls across components).
        if (globalState.context.id) identify(globalState.context.id);
        globalMethods.informContext();
    },
    buildTitle,
    updateSEOMeta
}

app.config.globalProperties.$state = globalState
app.config.globalProperties.$services = services
app.config.globalProperties.$methods = globalMethods
app.config.globalProperties.$filters = filters
app.config.globalProperties.$bus = EventBus

app.config.globalProperties.$notify = {
    success: (opts) => toast({ ...opts, type: 'success' }),
    error: (opts) => toast({ ...opts, type: 'error' }),
    info: (opts) => toast({ ...opts, type: 'info' }),
    warn: (opts) => toast({ ...opts, type: 'warn' }),
}

app.config.globalProperties.$linebreaks = filters.linebreaks
app.config.globalProperties.$percentage = filters.percentage

app.use(router)

app.mount('#app')

globalMethods.getContext();

EventBus.on('context:changed', globalMethods.getContext);
EventBus.on('context:updated', globalMethods.updateContext);
EventBus.on('context:request', globalMethods.informContext);

export { app, router, globalState, globalMethods, services }
